import React, { FC, useState } from 'react';
import { 
  Users, 
  ShieldCheck, 
  UserPlus, 
  Search, 
  Trash2, 
  Edit2, 
  Check, 
  X, 
  Mail, 
  Phone, 
  Calendar, 
  RefreshCw, 
  Key, 
  CheckCircle2, 
  Briefcase,
  Layers,
  Sparkles,
  Eye,
  AlertTriangle
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Employee } from '../types';

interface TeamManagementViewProps {
  employees: Employee[];
  onCreateEmployee: (newEmp: { name: string; role: string; email: string; phone: string; visibleTabs: string[]; password?: string }) => Promise<void>;
  onUpdateEmployee: (id: string, updatedFields: Partial<Employee>) => Promise<void>;
  onDeleteEmployee: (id: string) => Promise<void>;
  simulatedEmployeeId: string;
  onSetSimulatedEmployeeId: (id: string) => void;
  allTabs: { id: string; label: string; icon: any }[];
}

export const TeamManagementView: FC<TeamManagementViewProps> = ({
  employees,
  onCreateEmployee,
  onUpdateEmployee,
  onDeleteEmployee,
  simulatedEmployeeId,
  onSetSimulatedEmployeeId,
  allTabs
}) => {
  // Local states
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // Form fields for Add Employee
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('1234');
  const [selectedTabs, setSelectedTabs] = useState<string[]>(['summary', 'tasks']);

  // Edit fields
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editTabs, setEditTabs] = useState<string[]>([]);

  const [isLoadingAction, setIsLoadingAction] = useState<string | null>(null);
  const [successToast, setSuccessToast] = useState<string | null>(null);

  const triggerToast = (msg: string) => {
    setSuccessToast(msg);
    setTimeout(() => setSuccessToast(null), 3000);
  };

  // Reset form
  const resetForm = () => {
    setName('');
    setRole('');
    setEmail('');
    setPhone('');
    setPassword('1234');
    setSelectedTabs(['summary', 'tasks']);
  };

  // Submit new employee
  const handleAddNew = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !role) return;

    setIsLoadingAction('create');
    try {
      await onCreateEmployee({
        name,
        role,
        email,
        phone,
        visibleTabs: selectedTabs,
        password
      });
      triggerToast(`İşçi "${name}" uğurla bazaya əlavə edildi!`);
      setIsAdding(false);
      resetForm();
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingAction(null);
    }
  };

  // Start editing
  const startEditing = (emp: Employee) => {
    setEditingId(emp.id);
    setEditName(emp.name);
    setEditRole(emp.role);
    setEditEmail(emp.email);
    setEditPhone(emp.phone);
    setEditPassword(emp.password || '1234');
    setEditTabs(emp.visibleTabs || []);
  };

  // Save edits
  const handleSaveEdit = async (empId: string) => {
    if (!editName || !editRole) return;
    setIsLoadingAction(`edit-${empId}`);
    try {
      await onUpdateEmployee(empId, {
        name: editName,
        role: editRole,
        email: editEmail,
        phone: editPhone,
        visibleTabs: editTabs,
        password: editPassword
      });
      triggerToast(`İşçi "${editName}" məlumatları uğurla yadda saxlanıldı.`);
      setEditingId(null);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingAction(null);
    }
  };

  // Delete employee
  const handleDelete = async (empId: string, empName: string) => {
    if (!window.confirm(`"${empName}" adlı əməkdaşı bazadan tamamilə silmək istədiyinizə əminsiniz?`)) {
      return;
    }
    setIsLoadingAction(`delete-${empId}`);
    try {
      await onDeleteEmployee(empId);
      triggerToast(`Əməkdaş "${empName}" portaldan silindi.`);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingAction(null);
    }
  };

  // Toggle single permission tab selection (Add form)
  const toggleTabSelection = (tabId: string) => {
    if (selectedTabs.includes(tabId)) {
      setSelectedTabs(selectedTabs.filter(id => id !== tabId));
    } else {
      setSelectedTabs([...selectedTabs, tabId]);
    }
  };

  // Toggle single permission tab selection (Edit form)
  const toggleEditTabSelection = (tabId: string) => {
    if (editTabs.includes(tabId)) {
      setEditTabs(editTabs.filter(id => id !== tabId));
    } else {
      setEditTabs([...editTabs, tabId]);
    }
  };

  // Filter employees
  const filteredEmployees = employees.filter(emp => 
    emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (emp.email && emp.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-6 text-slate-800 animate-fade-in" id="team_management_container">
      
      {/* Visual Title Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <h2 className="text-xl md:text-2xl font-black text-slate-950 flex items-center gap-2" id="team_title">
            🛡️ Komanda və Giriş İcazələri
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Kompaniya operatorlarının hesablarını idarə edin, iş rollarını təyin edin və idarəetmə panelinin bölmələrinin hər bir işçi üçün görünməsini çevik şəkildə idarə edin.
          </p>
        </div>
        
        <button
          onClick={() => setIsAdding(!isAdding)}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 font-bold rounded-xl text-xs shadow-md shadow-indigo-600/10 cursor-pointer select-none transition-colors animate-pulse"
          id="btn_add_employee_toggle"
        >
          {isAdding ? <X className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
          {isAdding ? 'Anketi bağla' : 'İşçi əlavə et'}
        </button>
      </div>

      {/* Success alert message */}
      <AnimatePresence>
        {successToast && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-4 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-xl text-xs font-bold flex items-center gap-2 shadow-xs"
            id="toast_success"
          >
            <CheckCircle2 className="w-4.5 h-4.5 text-emerald-600 shrink-0" />
            <span>{successToast}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* IMPERSONATION SIMULATOR BANNER */}
      <div className="bg-slate-950 text-white rounded-2xl p-4 md:p-5 border border-slate-900 shadow-md relative overflow-hidden" id="simulator_banner">
        <div className="absolute right-0 top-0 w-48 h-48 bg-indigo-500/15 rounded-full blur-2xl pointer-events-none" />
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 relative z-10">
          <div className="space-y-1">
            <span className="text-[10px] text-indigo-400 font-mono font-bold uppercase tracking-wider block">
              💻 İcazələrin İnteraktiv Simulyatoru
            </span>
            <p className="text-sm font-bold text-white">
              Sistemin cari görünüşü:{' '}
              <span className="text-emerald-400 font-extrabold px-1.5 py-0.5 bg-slate-900 border border-slate-800 rounded font-mono text-xs">
                {simulatedEmployeeId === 'admin' 
                  ? '👑 Baş Administrator (Tam Giriş yetkisi)' 
                  : `👤 ${employees.find(e => e.id === simulatedEmployeeId)?.name || 'İşçi'} (${employees.find(e => e.id === simulatedEmployeeId)?.role || ''})`
                }
              </span>
            </p>
            <p className="text-[11px] text-slate-400">
              {simulatedEmployeeId === 'admin' 
                ? 'Siz CRM sisteminin bütün bölmələrini görürsünüz. Saydbar panellərinin real rejimdə gizlədilməsini yoxlamaq üçün sağdakı siyahıdan digər əməkdaşları seçib test edə bilərsiniz.'
                : 'Menyu bölmələri və saybardan əməkdaşın icazəsi olmayan səhifələr avtomatik olaraq gizlədilmişdir.'
              }
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-slate-400 font-bold font-mono">Necə görünür:</span>
            <select
              value={simulatedEmployeeId}
              onChange={(e) => onSetSimulatedEmployeeId(e.target.value)}
              className="bg-slate-900 border border-slate-800 text-white rounded-xl px-3 py-2 text-xs font-bold font-mono outline-hidden focus:border-indigo-500"
              id="select_impersonate"
            >
              <option value="admin">👑 Baş Administrator (Super-Giriş)</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  👤 {emp.name} ({emp.role})
                </option>
              ))}
            </select>

            {simulatedEmployeeId !== 'admin' && (
              <button
                type="button"
                onClick={() => onSetSimulatedEmployeeId('admin')}
                className="p-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-all cursor-pointer"
                title="Görünüşü Baş Administratora sıfırla"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ADD EMPLOYEE COLLAPSIBLE SLIDE FORM */}
      <AnimatePresence>
        {isAdding && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
            id="add_employee_form_wrapper"
          >
            <form onSubmit={handleAddNew} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
              <div className="flex items-center gap-2 border-b border-slate-200 pb-3">
                <UserPlus className="w-4.5 h-4.5 text-indigo-600" />
                <h3 className="font-extrabold text-sm text-slate-900">CRM-də yeni əməkdaş profili</h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                
                {/* 1. Full name */}
                <div className="space-y-1">
                  <label className="text-[10px] font-mono font-bold text-slate-500 uppercase">Əməkdaşın TAA (Ad, Soyad) *</label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Məsələn: Orxan Məmmədov"
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-slate-50/50 outline-hidden focus:border-indigo-400 focus:bg-white"
                  />
                </div>

                {/* 2. Position / Role */}
                <div className="space-y-1">
                  <label className="text-[10px] font-mono font-bold text-slate-500 uppercase">Vəzifə / İş Rolu *</label>
                  <input
                    type="text"
                    required
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="Məsələn: Səyyar Çəkiliş Prodüseri"
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-slate-50/50 outline-hidden focus:border-indigo-400 focus:bg-white"
                  />
                </div>

                {/* 3. Email */}
                <div className="space-y-1">
                  <label className="text-[10px] font-mono font-bold text-slate-500 uppercase">E-mail ünvanı</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="orxan.ma@baku-menu.az"
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-slate-50/50 outline-hidden focus:border-indigo-400 focus:bg-white"
                  />
                </div>

                {/* 4. Phone */}
                <div className="space-y-1">
                  <label className="text-[10px] font-mono font-bold text-slate-500 uppercase">Mobil telefon</label>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+994 (50) 678-88-22"
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-slate-50/50 outline-hidden focus:border-indigo-400 focus:bg-white"
                  />
                </div>

                {/* 5. Password code */}
                <div className="space-y-1">
                  <label className="text-[10px] font-mono font-bold text-slate-500 uppercase">Şifrə / PIN-kod *</label>
                  <input
                    type="text"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="1234"
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-slate-50/50 outline-hidden font-mono font-extrabold text-indigo-700 focus:border-indigo-400 focus:bg-white"
                  />
                </div>

              </div>

              {/* PERMISSION SELECTOR BOX */}
              <div className="space-y-2.5 pt-2">
                <div className="flex items-center gap-1">
                  <Key className="w-3.5 h-3.5 text-indigo-600" />
                  <span className="text-[10px] font-mono font-bold text-slate-500 uppercase">
                    Görünüş səviyyələrinin nizamlanması (Giriş icazəsi olan səhifələr) *
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {allTabs.map(tab => {
                    const isSelected = selectedTabs.includes(tab.id);
                    const Icon = tab.icon;
                    return (
                      <button
                        type="button"
                        key={tab.id}
                        onClick={() => toggleTabSelection(tab.id)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-left text-xs transition-colors cursor-pointer outline-hidden select-none ${
                          isSelected 
                            ? 'bg-indigo-50 border-indigo-300 text-indigo-800 font-extrabold' 
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50/80'
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full ${isSelected ? 'bg-indigo-600 scale-125' : 'bg-slate-300'}`} />
                        <Icon className="w-3.5 h-3.5 text-slate-450 shrink-0" />
                        <span className="truncate">{tab.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Form submit footer */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => {
                    setIsAdding(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-slate-200 hover:bg-slate-50/80 text-slate-600 text-xs font-bold rounded-xl cursor-pointer"
                >
                  İmtina
                </button>
                <button
                  type="submit"
                  disabled={isLoadingAction === 'create'}
                  className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 text-xs font-bold rounded-xl shadow-md cursor-pointer disabled:opacity-50"
                >
                  {isLoadingAction === 'create' ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Check className="w-3.5 h-3.5" />
                  )}
                  <span>Komandaya qəbul et</span>
                </button>
              </div>

            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* TEAM RATIOS & METRICS STATS */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" id="team_stats_grid">
        <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-center justify-between gap-3">
          <div className="space-y-0.5">
            <span className="text-[10px] text-slate-400 font-mono font-bold uppercase block tracking-wider">Ümumi əməkdaşlar</span>
            <span className="text-xl font-black font-mono text-slate-900">{employees.length} nəfər</span>
            <p className="text-[9px] text-slate-400">Rəqəmsallaşdırmada aktiv iştirakçılar</p>
          </div>
          <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl">
            <Users className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-center justify-between gap-3">
          <div className="space-y-0.5">
            <span className="text-[10px] text-slate-400 font-mono font-bold uppercase block tracking-wider">Superistifadəçilər</span>
            <span className="text-xl font-black font-mono text-slate-900">
              {employees.filter(e => e.visibleTabs.length === allTabs.length).length} nəfər
            </span>
            <p className="text-[9px] text-slate-400">Bütün CRM alətlərinə tam giriş hüququ</p>
          </div>
          <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl">
            <ShieldCheck className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-center justify-between gap-3">
          <div className="space-y-0.5">
            <span className="text-[10px] text-slate-400 font-mono font-bold uppercase block tracking-wider">Məhdudlaşdırılmış giriş</span>
            <span className="text-xl font-black font-mono text-slate-900">
              {employees.filter(e => e.visibleTabs.length < allTabs.length).length} nəfər
            </span>
            <p className="text-[9px] text-slate-400">Bəzi menyu səhifələri gizlədilib</p>
          </div>
          <div className="p-2.5 bg-amber-50 text-amber-600 rounded-xl">
            <Key className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* CORE TEAM LIST WITH PERMISSIONS MATRIX CONTROL */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden" id="employees_database_card">
        
        {/* Ledger Toolbar */}
        <div className="p-4 border-b border-slate-150 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="font-extrabold text-slate-900 text-sm">Əməkdaşların reyestri və icazələr matrisi</h3>
            <p className="text-[10px] text-slate-400 mt-0.5">Hər bir icraçının fərdi giriş hüquqlarının tənzimlənməsi</p>
          </div>

          {/* Search bar */}
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Ad və ya vəzifə üzrə axtarış..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 border border-slate-250 rounded-lg text-xs bg-white outline-hidden focus:border-indigo-400 placeholder:text-slate-400 font-medium"
            />
          </div>
        </div>

        {/* Dynamic List / Grid */}
        {filteredEmployees.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {filteredEmployees.map(emp => {
              const isEditing = editingId === emp.id;
              const isActionLoading = isLoadingAction?.startsWith(emp.id) || isLoadingAction === `delete-${emp.id}`;

              return (
                <div key={emp.id} className="p-5 hover:bg-slate-50/30 transition-colors flex flex-col md:flex-row gap-5 items-start justify-between">
                  
                  {/* Left segment: User name, role & contact credentials */}
                  <div className="flex gap-3.5 items-start max-w-sm">
                    <div className={`w-11 h-11 rounded-2xl ${emp.bg} text-white flex items-center justify-center font-bold text-sm shadow-inner shrink-0 mt-0.5 select-none`}>
                      {emp.avatar}
                    </div>

                    <div className="space-y-1">
                      {isEditing ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="w-full px-2 py-1 border border-indigo-300 rounded text-xs bg-white outline-indigo-550 font-extrabold text-slate-800"
                            placeholder="Ad, Soyad"
                          />
                          <input
                            type="text"
                            value={editRole}
                            onChange={(e) => setEditRole(e.target.value)}
                            className="w-full px-2 py-1 border border-indigo-200 rounded text-slate-700 text-[11px] outline-indigo-550 font-semibold"
                            placeholder="Vəzifə"
                          />
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-1.5 py-0.5">
                            <h4 className="font-extrabold text-slate-900 text-sm leading-none">{emp.name}</h4>
                            {emp.visibleTabs.length === allTabs.length && (
                              <span className="bg-emerald-50 text-emerald-700 text-[9px] font-bold px-1.5 py-0.5 rounded font-mono border border-emerald-100 select-none">
                                Super Administrator
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-500 font-semibold">{emp.role}</p>
                        </>
                      )}

                      {/* Contact items block */}
                      <div className="space-y-0.5 pt-1.5">
                        {isEditing ? (
                          <div className="space-y-1.5 font-medium">
                            <input
                              type="email"
                              value={editEmail}
                              onChange={(e) => setEditEmail(e.target.value)}
                              className="w-full px-2 py-0.5 border border-slate-200 rounded text-[10px] bg-white outline-indigo-400 text-slate-700"
                              placeholder="E-mail"
                            />
                            <input
                              type="text"
                              value={editPhone}
                              onChange={(e) => setEditPhone(e.target.value)}
                              className="w-full px-2 py-0.5 border border-slate-200 rounded text-[10px] bg-white outline-indigo-400 font-mono text-slate-700"
                              placeholder="Telefon"
                            />
                            <div className="space-y-0.5">
                              <span className="text-[8px] font-mono font-semibold text-slate-400 uppercase tracking-widest block select-none">Şifrə / PIN</span>
                              <input
                                type="text"
                                value={editPassword}
                                onChange={(e) => setEditPassword(e.target.value)}
                                className="w-full px-2 py-0.5 border border-slate-200 rounded text-[10px] bg-white outline-indigo-400 font-mono font-extrabold text-indigo-750"
                                placeholder="Şifrə"
                              />
                            </div>
                          </div>
                        ) : (
                          <>
                            {emp.email && (
                              <p className="text-[10px] text-slate-400 flex items-center gap-1 font-medium">
                                <Mail className="w-3 h-3 text-slate-400" />
                                <span>{emp.email}</span>
                              </p>
                            )}
                            {emp.phone && (
                              <p className="text-[10px] text-slate-400 flex items-center gap-1 font-mono">
                                <Phone className="w-3 h-3 text-slate-400" />
                                <span>{emp.phone}</span>
                              </p>
                            )}
                            <p className="text-[9px] text-slate-400 flex items-center gap-1 font-mono">
                              <Calendar className="w-3 h-3 text-slate-400" />
                              <span>Qəbul tarixi: {emp.createdAt || 'Məlum deyil'}</span>
                            </p>
                            <p className="text-[9px] text-indigo-700 font-semibold flex items-center gap-1 font-mono bg-indigo-50/70 border border-indigo-100/50 px-2 py-0.5 rounded w-max mt-1 select-none" title="E-mail olmadan sadə şifrə ilə daxil olmaq üçün istifadə edilir">
                              <Key className="w-3 h-3 text-indigo-500 shrink-0" />
                              <span>Giriş kodu: <span className="font-extrabold text-indigo-900 border-b border-indigo-300 border-dashed cursor-pointer select-all" title="Seçmək üçün iki dəfə klikləyin">{emp.password || '1234'}</span></span>
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Middle segment: Visual representation of permissions */}
                  <div className="flex-1 w-full space-y-2 text-left">
                    <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block select-none border-b border-transparent py-0.5">
                      İcazə verilən CRM bölmələri:
                    </span>
                    
                    {isEditing ? (
                      /* Selectable matrices in edit mode */
                      <div className="flex flex-wrap gap-1.5 p-3.5 bg-indigo-50/40 rounded-xl border border-indigo-100/50">
                        {allTabs.map(tab => {
                          const isAllowed = editTabs.includes(tab.id);
                          return (
                            <button
                              type="button"
                              key={tab.id}
                              onClick={() => toggleEditTabSelection(tab.id)}
                              className={`px-2.5 py-1 text-[10px] font-bold rounded-lg border flex items-center gap-1 transition-all cursor-pointer outline-hidden select-none ${
                                isAllowed
                                  ? 'bg-indigo-600 text-white border-indigo-700 font-black'
                                  : 'bg-white text-slate-400 border-slate-200 hover:bg-slate-50'
                              }`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${isAllowed ? 'bg-white' : 'bg-slate-300'}`} />
                              {tab.label}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      /* Read-only list of permission badges with elegant light contrast */
                      <div className="flex flex-wrap gap-1.5 max-w-lg select-none">
                        {allTabs.map(tab => {
                          const isAllowed = emp.visibleTabs && emp.visibleTabs.includes(tab.id);
                          const Icon = tab.icon;
                          return (
                            <span
                              key={tab.id}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold border ${
                                isAllowed 
                                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800 font-extrabold' 
                                  : 'bg-slate-50/50 border-slate-100 text-slate-400 line-through decoration-slate-350 opacity-60'
                              }`}
                            >
                              <Icon className={`w-3 h-3 shrink-0 ${isAllowed ? 'text-emerald-500' : 'text-slate-305'}`} />
                              <span>{tab.label}</span>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Right segment: Control, Action & Test simulation options */}
                  <div className="flex items-center gap-2 self-stretch md:self-center justify-end border-t md:border-t-0 border-slate-100 pt-3 md:pt-0 shrink-0 w-full md:w-auto select-none">
                    {isEditing ? (
                      <div className="flex gap-2.5">
                        <button
                          type="button"
                          onClick={() => setEditingId(null)}
                          className="px-2.5 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-600 text-[10px] font-bold rounded-lg cursor-pointer"
                        >
                          İmtina
                        </button>
                        <button
                          type="button"
                          disabled={isActionLoading}
                          onClick={() => handleSaveEdit(emp.id)}
                          className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-bold rounded-lg cursor-pointer flex items-center gap-1"
                        >
                          {isActionLoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          <span>Yadda saxla</span>
                        </button>
                      </div>
                    ) : (
                      <div className="flex md:flex-col lg:flex-row gap-2 w-full justify-between md:justify-end">
                        
                        {/* Simulation trigger */}
                        <button
                          type="button"
                          onClick={() => {
                            onSetSimulatedEmployeeId(emp.id);
                            triggerToast(`Simulyator aktivləşdirildi! Saydbar "${emp.name}" icazələrinə uyğun yenidən quruldu.`);
                          }}
                          className={`px-2.5 py-1.5 border rounded-lg hover:shadow-2xs text-[10px] font-bold flex items-center gap-1.5 transition-all cursor-pointer select-none outline-hidden ${
                            simulatedEmployeeId === emp.id 
                              ? 'bg-emerald-500 border-emerald-600 text-white font-extrabold' 
                              : 'bg-white border-slate-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-200'
                          }`}
                          title="İnterfeysin bu əməkdaş üçün necə göründüyünü yoxla"
                        >
                          <Eye className="w-3.5 h-3.5 shrink-0" />
                          <span>{simulatedEmployeeId === emp.id ? 'Test rejimində' : 'İcayəni yoxla'}</span>
                        </button>

                        <div className="flex gap-1.5">
                          {/* Edit button */}
                          <button
                            type="button"
                            onClick={() => startEditing(emp)}
                            className="p-1.5 border border-slate-200 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 hover:border-indigo-150 rounded-lg transition-colors cursor-pointer"
                            title="Əməkdaş məlumatlarını və icazələrini dəyişdir"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>

                          {/* Delete button */}
                          <button
                            type="button"
                            onClick={() => handleDelete(emp.id, emp.name)}
                            disabled={isActionLoading}
                            className="p-1.5 border border-slate-200 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                            title="Əməkdaşı CRM-dən sil"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>

                      </div>
                    )}
                  </div>

                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-12 text-center space-y-2 max-w-sm mx-auto select-none">
            <Users className="w-10 h-10 text-slate-300 mx-auto animate-bounce" />
            <h4 className="font-bold text-sm text-slate-700">Heç kim tapılmadı</h4>
            <p className="text-xs text-slate-400">
              Axtarış sorğunuza uyğun əməkdaşlar arasında heç bir nəticə tapılmadı.
            </p>
          </div>
        )}

      </div>

      {/* ADDITIONAL SECURE MEMO */}
      <div className="p-4 bg-indigo-50/45 border border-indigo-100 rounded-2xl flex items-start gap-3 text-left">
        <AlertTriangle className="w-4.5 h-4.5 text-indigo-550 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="text-xs font-bold text-indigo-950">Giriş icazələri dəyişikliklərinin avtomatik qeydiyyatı</p>
          <p className="text-[10px] text-indigo-800 leading-relaxed">
            Mərkəzi ofisin korporativ təhlükəsizlik standartlarına uyğun olaraq, hər bir rol dəyişikliyi, naviqasiya icazələri və ya yeni əməkdaş qəbulu IP ünvanı və operatorun adı ilə birlikdə Sistem Auditində (Sistem Jurnalları) qeyd olunur. Dəyişikliklər bütün şəbəkədə anında qüvvəyə minir.
          </p>
        </div>
      </div>

    </div>
  );
};
